// components/alerts/AlertPanel.tsx
// Slide-out panel showing recent alerts with acknowledge actions

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlerts } from '../../contexts/AlertContext';
import type { Alert } from '../../services/omoreAlertingService';
import './AlertPanel.css';

const SEVERITY_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
    critical: { bg: '#dc262620', text: '#dc2626' },
    high: { bg: '#ef444420', text: '#ef4444' },
    medium: { bg: '#f59e0b20', text: '#f59e0b' },
    low: { bg: '#9ca3af20', text: '#9ca3af' },
};

const CATEGORY_LABELS: Record<string, string> = {
    self_harm: 'Self-Harm',
    violence: 'Violence',
    sexual: 'Sexual',
    harassment: 'Harassment',
    hate: 'Hate',
    inappropriate: 'Inappropriate',
    prompt_injection: 'Prompt Injection',
    other: 'Other',
};

export const AlertPanel: React.FC = () => {
    const navigate = useNavigate();
    const {
        alerts,
        unacknowledgedCount,
        acknowledgeAlertById,
        acknowledgeAll,
        isAlertPanelOpen,
        setAlertPanelOpen,
    } = useAlerts();

    if (!isAlertPanelOpen) return null;

    const formatTimestamp = (ts: any): string => {
        if (!ts) return 'N/A';
        try {
            const date = ts.toDate ? ts.toDate() : new Date(ts);
            return date.toLocaleString();
        } catch {
            return 'N/A';
        }
    };

    const handleViewDetails = (alert: Alert) => {
        navigate(`/omore/sessions/${alert.sessionID}`);
        setAlertPanelOpen(false);
    };

    return (
        <div className="alert-panel-overlay" onClick={() => setAlertPanelOpen(false)}>
            <div className="alert-panel" onClick={e => e.stopPropagation()}>
                <div className="alert-panel-header">
                    <h2>
                        Alerts
                        {unacknowledgedCount > 0 && (
                            <span className="alert-count-badge">{unacknowledgedCount}</span>
                        )}
                    </h2>
                    <div className="alert-panel-actions">
                        {unacknowledgedCount > 0 && (
                            <button onClick={acknowledgeAll} className="mark-all-btn">
                                Mark All Read
                            </button>
                        )}
                        <button onClick={() => navigate('/alerts/settings')} className="settings-btn">
                            Settings
                        </button>
                        <button onClick={() => setAlertPanelOpen(false)} className="close-btn">×</button>
                    </div>
                </div>

                <div className="alert-panel-body">
                    {alerts.length === 0 && (
                        <div className="alert-empty">
                            <div className="alert-empty-icon">✓</div>
                            <p>No alerts</p>
                        </div>
                    )}

                    {alerts.map(alert => {
                        const badgeColors = SEVERITY_BADGE_COLORS[alert.severity] || SEVERITY_BADGE_COLORS.low;
                        return (
                            <div
                                key={alert.id}
                                className={`alert-item ${alert.acknowledged ? 'acknowledged' : 'unacknowledged'}`}
                            >
                                <div className="alert-item-header">
                                    <span
                                        className="severity-tag"
                                        style={{ background: badgeColors.bg, color: badgeColors.text }}
                                    >
                                        {alert.severity.toUpperCase()} ({alert.severityScore}/5)
                                    </span>
                                    <span className="alert-time">{formatTimestamp(alert.sentAt)}</span>
                                </div>

                                <div className="alert-item-categories">
                                    {alert.flagCategories.map(cat => (
                                        <span key={cat} className="alert-cat-tag">
                                            {CATEGORY_LABELS[cat] || cat}
                                        </span>
                                    ))}
                                </div>

                                <div className="alert-item-info">
                                    <span>User: {alert.explorerName}</span>
                                    <span>Helper: {alert.helperType}</span>
                                </div>

                                <div className="alert-item-content">
                                    {alert.content.length > 120
                                        ? alert.content.slice(0, 120) + '...'
                                        : alert.content}
                                </div>

                                <div className="alert-item-actions">
                                    {!alert.acknowledged && (
                                        <button
                                            onClick={() => acknowledgeAlertById(alert.id)}
                                            className="ack-btn"
                                        >
                                            Acknowledge
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleViewDetails(alert)}
                                        className="view-btn"
                                    >
                                        View Details
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
